import _, { get } from "lodash";
import startCase from "lodash/startCase";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ClientContext } from "../App";
import {
  DetailsPage,
  DetailsPageCard,
  DetailsPageColumn,
  DetailsPageField,
} from "../components/DetailsPage";
import LoadingSpinner from "../components/LoadingSpinner";
import PolicyCard from "../components/PolicyCard";
import { getAccount, getIgnitionSession, getUser } from "../lib/automatic";
import { handleForbiddenError } from "../lib/errorHandler";

// Skeleton placeholder for loading cards (matches PolicyValidation pattern)
const CardSkeleton = ({ lines = 3 }) => (
  <div className="flex flex-col animate-pulse gap-y-4">
    {[...Array(lines)].map((_, i) => (
      <div key={i} className="flex flex-col gap-2">
        <div className="h-3 bg-gray-200 rounded w-16 uppercase" />
        <div className="h-4 bg-gray-100 rounded w-full max-w-xs" />
      </div>
    ))}
  </div>
);

const PoliciesSkeleton = () => (
  <div className="flex flex-row gap-x-4 overflow-scroll -mr-8 animate-pulse">
    {[...Array(1)].map((_, i) => (
      <div
        key={i}
        className="h-40 min-w-[16rem] bg-gray-100 rounded border border-gray-200"
      />
    ))}
  </div>
);

const formatOverviewDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        timeZoneName: "short",
      })
    : "—";

/**
 * Carrier and policy number come from the Proxy Ignition lookup attempt, so only proxy
 * sessions get those two columns. Non-proxy sessions send an explicit null attempt, so
 * both fields are read through a lodash path rather than off the attempt object.
 */
export const getOverviewColumns = (ignitionSession) => {
  const carrier = get(ignitionSession, "proxyPolicyLookupAttempt.carrier");
  const policyNumber = get(
    ignitionSession,
    "proxyPolicyLookupAttempt.policyNumber"
  );

  return [
    carrier && { label: "Carrier", value: startCase(carrier) },
    policyNumber && {
      label: "Policy Number",
      value: policyNumber,
    },
    { label: "Result", value: ignitionSession.resultLabel ?? "—" },
    { label: "Result Detail", value: ignitionSession.resultDetailLabel ?? "—" },
    {
      label: "Last Update",
      value: formatOverviewDate(
        get(ignitionSession, "modifiedAt") ?? get(ignitionSession, "createdAt")
      ),
    },
  ].filter(Boolean);
};

const IgnitionSessionDetails = () => {
  let params = useParams();
  const [ignitionSession, setIgnitionSession] = useState();
  const [user, setUser] = useState();
  const [account, setAccount] = useState();
  const [policies, setPolicies] = useState();
  const { selectedClient: clientId } = useContext(ClientContext);
  const [isLoading, setIsLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);

  const fetchData = async (ignitionToken) => {
    const [fetchedIgnitionSession, error] = await getIgnitionSession(
      ignitionToken,
      clientId
    );
    if (error) {
      switch (get(error, "code")) {
        case 403:
        case 400:
          handleForbiddenError(error);
          break;
        default:
          console.error(
            "Error fetching ignition session details:",
            get(error, "message", error)
          );
      }
      return;
    }

    setIgnitionSession(fetchedIgnitionSession);
    setIsLoading(false);

    if (fetchedIgnitionSession.user) {
      setUserLoading(true);
      const [fetchedUser, userError] = await getUser(
        get(fetchedIgnitionSession, "user.id"),
        clientId
      );
      setUserLoading(false);
      if (userError) {
        switch (get(userError, "code")) {
          case 403:
            handleForbiddenError(userError);
            break;
          default:
            console.error(
              "Error fetching user details:",
              get(userError, "message", userError)
            );
        }
      } else {
        setUser(fetchedUser);
      }
    }

    if (fetchedIgnitionSession.account) {
      setAccountLoading(true);
      const [fetchedAccount, accountError] = await getAccount(
        fetchedIgnitionSession.account,
        { expand: true },
        clientId
      );
      setAccountLoading(false);
      if (accountError) {
        switch (get(accountError, "code")) {
          case 403:
            handleForbiddenError(accountError);
            break;
          default:
            console.error(
              "Error fetching account details:",
              get(accountError, "message", accountError)
            );
        }
      } else {
        setAccount(fetchedAccount);
        setPolicies(fetchedAccount?.policies);
      }
    }
  };

  useEffect(() => {
    if (!clientId) return;
    fetchData(params.ignitionToken);
  }, [clientId, params.ignitionToken]);

  const proxyIgnitionErrorMessage = get(
    ignitionSession,
    "proxyPolicyLookupAttempt.errorMessage"
  );

  const renderDetails = ignitionSession && (
    <DetailsPage
      title={ignitionSession.id}
      badge={ignitionSession.status}
      actions={[
        {
          type: "copy",
          label: "Copy ignition link",
          value: `https://ignition${
            process.env.REACT_APP_STAGE === "prod"
              ? ""
              : `.${process.env.REACT_APP_STAGE}`
          }.axle.insure/?token=${ignitionSession.id}`,
        },
        {
          type: "copy",
          label: "Copy ignition token",
          value: ignitionSession.id,
        },
      ]}
    >
      {/* Primary column */}
      <DetailsPageColumn primary>
        {/* Session Overview */}
        <DetailsPageCard title="Overview">
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-row md:divide-x gap-x-6">
              {getOverviewColumns(ignitionSession).map(({ label, value }) => (
                <DetailsPageField
                  key={label}
                  label={label}
                  value={value}
                  className="pl-6 first:pl-0"
                />
              ))}
            </div>
            {/* only show Proxy Ignition error message if an error message is present.
                Full width rather than another column so long messages stay readable. */}
            {proxyIgnitionErrorMessage ? (
              <DetailsPageField
                label="Error Message"
                value={proxyIgnitionErrorMessage}
              />
            ) : null}
          </div>
        </DetailsPageCard>

        {/* Policies — show when session has account; skeleton until account is loaded */}
        {ignitionSession.account && (
          <DetailsPageCard title="Policies">
            {accountLoading || !account ? (
              <PoliciesSkeleton />
            ) : policies?.length ? (
              <div className="flex flex-row gap-x-4 overflow-scroll -mr-8">
                {policies.map((policy) => (
                  <PolicyCard policy={policy} key={policy.id} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No policies.</p>
            )}
          </DetailsPageCard>
        )}

        {/* Session Details*/}
        <DetailsPageCard title="Session">
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row">
              <div className="flex flex-col w-44 gap-y-2 text-gray-600">
                <p className="text-sm">Type</p>
              </div>
              <div className="flex flex-col grow">
                <p className="text-sm">
                  {_(ignitionSession).get("type") ?? "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-row">
              <div className="flex flex-col min-w-[11rem] gap-y-2 text-gray-600">
                <p className="text-sm">Link</p>
              </div>
              <div className="flex flex-col grow">
                <p className="text-sm">
                  {_(ignitionSession)
                    .chain()
                    .get("id")
                    .thru(
                      (token) =>
                        `https://ignition${
                          process.env.REACT_APP_STAGE === "prod"
                            ? ""
                            : `.${process.env.REACT_APP_STAGE}`
                        }.axle.insure/?token=${token}`
                    )
                    .value() ?? "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-row">
              <div className="flex flex-col w-44 gap-y-2 text-gray-600">
                <p className="text-sm">Redirect URL</p>
              </div>
              <div className="flex flex-col grow">
                <p className="text-sm">
                  {_(ignitionSession).get("redirectUri") ?? "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-row">
              <div className="flex flex-col w-44 gap-y-2 text-gray-600">
                <p className="text-sm">Webhook URL</p>
              </div>
              <div className="flex flex-col grow">
                <p className="text-sm">
                  {_(ignitionSession).get("webhookUri") ?? "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-row">
              <div className="flex flex-col w-44 gap-y-2 text-gray-600">
                <p className="text-sm">Created</p>
              </div>
              <div className="flex flex-col grow">
                <p className="text-sm">
                  {_(ignitionSession).get("createdAt")
                    ? new Date(
                        _(ignitionSession).get("createdAt")
                      ).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        timeZoneName: "short",
                      })
                    : "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-row">
              <div className="flex flex-col w-44 gap-y-2 text-gray-600">
                <p className="text-sm">Last modified</p>
              </div>
              <div className="flex flex-col grow">
                <p className="text-sm">
                  {_(ignitionSession).get("modifiedAt")
                    ? new Date(
                        _(ignitionSession).get("modifiedAt")
                      ).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        timeZoneName: "short",
                      })
                    : "—"}
                </p>
              </div>
            </div>
          </div>
        </DetailsPageCard>
      </DetailsPageColumn>

      {/* Secondary column — User card when session has user; skeleton while user is loading */}
      <DetailsPageColumn secondary>
        {ignitionSession.user && (
          <DetailsPageCard title="User">
            {userLoading ? (
              <CardSkeleton lines={3} />
            ) : user ? (
              <div className="flex flex-col divide-y gap-y-2">
                <div className="flex flex-col">
                  <h3 className="uppercase text-[0.625rem] font-medium">
                    Name
                  </h3>
                  <p className="text-sm">
                    {startCase(user.firstName)} {startCase(user.lastName)}
                  </p>
                </div>
                <div className="flex flex-col pt-2">
                  <h3 className="uppercase text-[0.625rem] font-medium">
                    Phone
                  </h3>
                  <p className="text-sm">{user.phone}</p>
                </div>
                <div className="flex flex-col pt-2">
                  <h3 className="uppercase text-[0.625rem] font-medium">
                    Email
                  </h3>
                  <p className="text-sm">{user.email ?? "—"}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Failed to load user details.
              </p>
            )}
          </DetailsPageCard>
        )}
      </DetailsPageColumn>
    </DetailsPage>
  );

  return <>{isLoading ? <LoadingSpinner /> : renderDetails}</>;
};

export default IgnitionSessionDetails;
